/**
 * Per-practice brand colors. A single "main" color drives the top-bar gradient;
 * the "accent" color is used solid (the thin line under the bar, active pills).
 * Values are applied as CSS variables on :root so the whole app updates live.
 */

export const DEFAULT_PRIMARY = '#152642';
export const DEFAULT_ACCENT = '#c9a227';

function clamp(n) {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function parseHex(hex) {
  let h = String(hex || '').trim().replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16)
  };
}

function toHex({ r, g, b }) {
  return '#' + [r, g, b].map((v) => clamp(v).toString(16).padStart(2, '0')).join('');
}

// Positive amount lightens toward white, negative darkens toward black.
function shade(hex, amount) {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const t = amount < 0 ? 0 : 255;
  const p = Math.abs(amount);
  return toHex({
    r: rgb.r + (t - rgb.r) * p,
    g: rgb.g + (t - rgb.g) * p,
    b: rgb.b + (t - rgb.b) * p
  });
}

// Build a three-stop diagonal gradient from a single main color.
export function deriveGradient(primary) {
  const base = parseHex(primary) ? primary : DEFAULT_PRIMARY;
  const dark = shade(base, -0.5);
  const light = shade(base, 0.18);
  return `linear-gradient(135deg, ${dark} 0%, ${base} 55%, ${light} 100%)`;
}

// Resolve a stored brand color to a valid hex, falling back to the default.
// Used where a real value is needed (e.g. AntD's colorPrimary token), not a CSS var.
export function resolvePrimary(hex) {
  return parseHex(hex) ? hex : DEFAULT_PRIMARY;
}

// Perceived brightness (YIQ, 0-255).
function brightness(rgb) {
  return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
}

// Readable text color to put ON a brand-colored fill (button bg, pills): dark
// ink for light brands, white for dark ones, so the label never washes out.
export function onColor(hex) {
  const rgb = parseHex(hex) || parseHex(DEFAULT_PRIMARY);
  return brightness(rgb) >= 150 ? '#1f1f1f' : '#ffffff';
}

// A version of the brand color dark enough to read as TEXT on a white surface
// (links, text/ghost buttons). Light brands get darkened toward black.
export function brandInk(hex) {
  let cur = parseHex(hex) ? hex : DEFAULT_PRIMARY;
  for (let i = 0; i < 8 && brightness(parseHex(cur)) > 140; i++) {
    cur = shade(cur, -0.15);
  }
  return cur;
}

// Apply the resolved brand colors as CSS variables on the document root.
export function applyBranding(branding = {}) {
  const primary = parseHex(branding.primaryColor) ? branding.primaryColor : DEFAULT_PRIMARY;
  const accent = parseHex(branding.accentColor) ? branding.accentColor : DEFAULT_ACCENT;
  const root = document.documentElement;
  root.style.setProperty('--brand-primary', primary);
  root.style.setProperty('--brand-grad', deriveGradient(primary));
  root.style.setProperty('--brand-accent', accent);
  root.style.setProperty('--brand-on-primary', onColor(primary));
  root.style.setProperty('--brand-on-accent', onColor(accent));
  root.style.setProperty('--brand-ink', brandInk(primary));
}
